/**
 * AppEventBus — the host side of the `emitEvent` ActionFlow action (Lane B-3).
 *
 * The engine's `onEmitEvent(name, payload)` hook fans a named app event out to
 * subscribers (sibling blocks / host components). This is a tiny synchronous
 * pub/sub keyed by event name. Listener exceptions are isolated (logged, never
 * abort the emit) so one bad subscriber can't break an action flow.
 */

export type AppEventListener = (payload?: unknown) => void

export class AppEventBus {
  private readonly listeners = new Map<string, Set<AppEventListener>>()

  /** Subscribe to a named event. Returns an unsubscribe function. */
  subscribe(name: string, listener: AppEventListener): () => void {
    let set = this.listeners.get(name)
    if (!set) {
      set = new Set()
      this.listeners.set(name, set)
    }
    set.add(listener)
    return () => {
      const s = this.listeners.get(name)
      if (!s) return
      s.delete(listener)
      if (s.size === 0) this.listeners.delete(name)
    }
  }

  /** Fire a named event. No-op when there are no subscribers. */
  emit(name: string, payload?: unknown): void {
    const set = this.listeners.get(name)
    if (!set || set.size === 0) return
    // Snapshot so a listener that (un)subscribes during dispatch is safe.
    for (const listener of [...set]) {
      try {
        listener(payload)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`[AppEventBus] listener for "${name}" threw`, err)
      }
    }
  }

  /** Number of listeners for a name (for tests/diagnostics). */
  listenerCount(name: string): number {
    return this.listeners.get(name)?.size ?? 0
  }

  clear(): void {
    this.listeners.clear()
  }
}
